import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getFileDisplayName, formatFileSize, getFileExtension } from '../../core/utils/fileUtils';

const FilePreview = ({ file, isOpen, onClose }) => {
  const [error, setError] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [previewImageLoaded, setPreviewImageLoaded] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // Reset zoom and position when opening
      setImageZoom(1);
      setImagePosition({ x: 0, y: 0 });
      setError(null);
      setPreviewImageLoaded(false);
      
      // Add escape key listener
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      
      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen || !file) return null;

  const fileExtension = getFileExtension(file.file_url || file.file_name || '');
  const displayName = getFileDisplayName(file);
  const downloadName = file.download_name || displayName;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = file.file_url;
    link.download = downloadName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Image zoom and drag handlers
  const handleImageWheel = (e) => {
    e.preventDefault();
    const zoomFactor = 0.1;
    const newZoom = e.deltaY > 0 
      ? Math.max(0.5, imageZoom - zoomFactor)
      : Math.min(3, imageZoom + zoomFactor);
    setImageZoom(newZoom);
  };

  const handleImageMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
  };

  const handleImageMouseMove = (e) => {
    if (isDragging) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleImageMouseUp = () => {
    setIsDragging(false);
  };

  const resetImageTransform = () => {
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const renderPreview = () => {
    const extension = fileExtension.toLowerCase();
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
      return (
        <div 
          className="flex items-center justify-center max-h-full overflow-hidden relative cursor-move"
          onWheel={handleImageWheel}
          onMouseMove={handleImageMouseMove}
          onMouseUp={handleImageMouseUp}
          onMouseLeave={handleImageMouseUp}
        >
          <img 
            src={file.file_url} 
            alt={displayName}
            className="max-w-full max-h-full object-contain select-none"
            style={{
              transform: `scale(${imageZoom}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
              transition: isDragging ? 'none' : 'transform 0.1s ease-out, opacity 0.35s ease-in-out',
              cursor: imageZoom > 1 ? 'move' : 'zoom-in',
              opacity: previewImageLoaded ? 1 : 0,
            }}
            onMouseDown={handleImageMouseDown}
            onLoad={() => setPreviewImageLoaded(true)}
            onError={() => { setPreviewImageLoaded(true); setError('Erro ao carregar imagem'); }}
            draggable={false}
          />
          {file.file_url && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(200, 200, 200, 0.15)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                borderRadius: "inherit",
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: 1,
                opacity: previewImageLoaded ? 0 : 1,
                transition: "opacity 0.3s ease-out",
              }}
              aria-hidden="true"
            >
              <span
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
                  animation: "progressive-img-shimmer 1.5s ease-in-out infinite",
                }}
              />
            </div>
          )}
          {imageZoom !== 1 && (
            <button
              onClick={resetImageTransform}
              className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-lg hover:bg-opacity-70 transition-all"
              title="Resetar zoom"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
        </div>
      );
    }
    
    // PDF files
    if (extension === 'pdf') {
      return (
        <div className="w-full h-full">
          <iframe
            src={`${file.file_url}#toolbar=1&navpanes=1&scrollbar=1`}
            className="w-full h-full border-0"
            title={displayName}
            onError={() => setError('Erro ao carregar PDF')}
          />
        </div>
      );
    }
    
    // Text files
    if (['txt', 'md', 'json', 'xml', 'csv', 'log'].includes(extension)) {
      return (
        <div className="w-full h-full bg-gray-50 p-4 overflow-auto">
          <iframe
            src={file.file_url}
            className="w-full h-full border-0 bg-white"
            title={displayName}
            onError={() => setError('Erro ao carregar arquivo de texto')}
          />
        </div>
      );
    }
    
    // Audio files
    if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(extension)) {
      return (
        <div className="flex flex-col items-center justify-center p-8">
          <div className="text-6xl mb-4">🎵</div>
          <audio controls className="w-full max-w-md">
            <source src={file.file_url} type={`audio/${extension === 'mp3' ? 'mpeg' : extension}`} />
            Seu navegador não suporta reprodução de áudio.
          </audio>
        </div>
      );
    }
    
    // Video files
    if (['mp4', 'webm', 'ogg', 'avi', 'mov'].includes(extension)) {
      return (
        <div className="flex items-center justify-center max-h-full">
          <video controls className="max-w-full max-h-full">
            <source src={file.file_url} type={`video/${extension}`} />
            Seu navegador não suporta reprodução de vídeo.
          </video>
        </div>
      );
    }
    
    // Office documents - try to use Google Docs viewer
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) {
      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(file.file_url)}&embedded=true`;
      return (
        <div className="w-full h-full">
          <iframe
            src={viewerUrl}
            className="w-full h-full border-0"
            title={displayName}
            onError={() => setError('Erro ao carregar documento. Arquivo pode não estar acessível publicamente.')}
          />
        </div>
      );
    }
    
    // Code files
    if (['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'py', 'java', 'cpp', 'c', 'php', 'rb', 'go', 'rs', 'sh'].includes(extension)) {
      return (
        <div className="w-full h-full bg-gray-50 p-4 overflow-auto">
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm">
            <iframe
              src={file.file_url}
              className="w-full h-full border-0 bg-transparent text-green-400"
              title={displayName}
              style={{ minHeight: '400px' }}
              onError={() => setError('Erro ao carregar código')}
            />
          </div>
        </div>
      );
    }
    
    // Default - show file info and download option
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="text-6xl mb-4">📄</div>
        <h3 className="text-xl font-medium text-gray-900 mb-2">{displayName}</h3>
        <p className="text-gray-600 mb-4">
          Preview não disponível para arquivos {extension.toUpperCase()}
        </p>
        <p className="text-sm text-gray-500 mb-6">
          {file.description && (
            <span className="block mb-2">{file.description}</span>
          )}
          {file.file_size && (
            <span>Tamanho: {formatFileSize(file.file_size)}</span>
          )}
        </p>
        <button
          onClick={handleDownload}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Baixar Arquivo
        </button>
      </div>
    );
  };

  const modal = (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className={`bg-white dark:bg-dark-900 rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col border border-gray-200 dark:border-dark-700`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-700">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white truncate">
              {displayName}
            </h2>
          </div>
          
          <div className="flex items-center space-x-2 ml-4">
            {/* Image controls */}
            {['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileExtension.toLowerCase()) && (
              <>
                <button
                  onClick={() => setImageZoom(Math.max(0.5, imageZoom - 0.2))}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Reduzir zoom"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  </svg>
                </button>
                <span className="text-sm text-gray-600 px-2">{Math.round(imageZoom * 100)}%</span>
                <button
                  onClick={() => setImageZoom(Math.min(3, imageZoom + 0.2))}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Aumentar zoom"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </button>
                <button
                  onClick={resetImageTransform}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Resetar zoom"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <div className="w-px h-6 bg-gray-300 mx-2"></div>
              </>
            )}
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Fechar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {error ? (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Erro ao carregar preview</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={handleDownload}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Baixar Arquivo
              </button>
            </div>
          ) : (
            renderPreview()
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default FilePreview;
